/**
 * assets/product-card-fashionhub.js
 * Sannah FashionHub — <product-card-fashionhub>
 *
 * What this file does NOT do anymore: call /cart/add.js itself. That's now
 * handled by Dawn's own <product-form> (see snippets/buy-buttons.liquid +
 * assets/product-form.js), which this custom element sits alongside inside
 * the card. product-form.js must be loaded globally (theme.liquid) for that
 * to work on collection/homepage grids — see the note in card-product.liquid.
 *
 * What this file DOES do:
 *   - Wishlist: localStorage under `sfh_wishlist`, same as before
 *   - Size selection: writes the chosen variant's id into the hidden
 *     `input.product-variant-id` that buy-buttons.liquid renders, so
 *     Dawn's form submits the *right* variant instead of the product's
 *     default one
 *   - Size-required guard: blocks the actual form submission (not just a
 *     custom click handler) if sizes exist and none is selected yet
 *   - Live price + image swap on size select, mirroring what the PDP's
 *     variant picker does, scaled down for a grid card
 */

const WISHLIST_KEY = "sfh_wishlist";

function getWishlist() {
  try {
    return JSON.parse(localStorage.getItem(WISHLIST_KEY)) || [];
  } catch {
    return [];
  }
}

function setWishlist(list) {
  localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
}

class ProductCardFashionhub extends HTMLElement {
  connectedCallback() {
    this.handle = this.dataset.productHandle;
    this.hasSizes = this.dataset.hasSizes === "true";
    this.selectedSize = null;

    const variantScript = this.querySelector("[data-sfh-variants]");
    this.variants = variantScript ? JSON.parse(variantScript.textContent) : [];

    // Elements owned by us
    this.wishlistBtn = this.querySelector("[data-sfh-wishlist-toggle]");
    this.sizeRow = this.querySelector("[data-sfh-size-row]");
    this.sizeWrapper = this.querySelector(".sfh-card__sizes");
    this.sizeError = this.querySelector("[data-sfh-size-error]");
    this.priceEl = this.querySelector("[data-sfh-price]");
    this.comparePriceEl = this.querySelector("[data-sfh-compare-price]");
    this.saleBadge = this.querySelector("[data-sfh-sale-badge]");
    this.mainImg = this.querySelector("[data-sfh-main-img]");

    // Elements owned by Dawn's buy-buttons.liquid output
    this.variantInput = this.querySelector("input.product-variant-id");
    this.submitBtn = this.querySelector(".product-form__submit");
    this.form = this.querySelector("form[data-type='add-to-cart-form']");

    this._syncWishlistUI();

    if (this.wishlistBtn) {
      this.wishlistBtn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        this._toggleWishlist();
      });
    }

    if (this.sizeRow) {
      this.sizeRow.querySelectorAll("[data-sfh-size]").forEach((btn) => {
        btn.addEventListener("click", () => {
          if (btn.disabled) return;
          this.selectedSize = btn.dataset.sfhSize;
          this.sizeRow.querySelectorAll("[data-sfh-size]").forEach((b) => {
            b.classList.toggle("is-selected", b === btn);
          });
          this._clearSizeError();
          this._applyVariant(this.selectedSize);
        });
      });
    }

    // Block submission (not just a click) when a size is required but
    // unselected — this is what actually stops product-form.js from
    // firing its /cart/add.js call, unlike a click-only guard.
    if (this.form) {
      this.form.addEventListener(
        "submit",
        (e) => {
          if (this.hasSizes && !this.selectedSize) {
            e.preventDefault();
            e.stopImmediatePropagation();
            this._showSizeError();
          }
        },
        { capture: true }
      );
    }
  }

  // ── Wishlist ────────────────────────────────────────────────────────
  _syncWishlistUI() {
    const wishlisted = getWishlist().includes(this.handle);
    if (this.wishlistBtn) {
      this.wishlistBtn.classList.toggle("is-wishlisted", wishlisted);
      this.wishlistBtn.setAttribute(
        "aria-label",
        wishlisted ? "Remove from wishlist" : "Add to wishlist"
      );
    }
  }

  _toggleWishlist() {
    const list = getWishlist();
    const idx = list.indexOf(this.handle);
    if (idx > -1) {
      list.splice(idx, 1);
    } else {
      list.push(this.handle);
    }
    setWishlist(list);
    this._syncWishlistUI();
    document.dispatchEvent(
      new CustomEvent("sfh:wishlist:updated", { detail: { wishlist: list } })
    );
  }

  // ── Size validation ─────────────────────────────────────────────────
  _showSizeError() {
    if (!this.sizeWrapper) return;
    this.sizeWrapper.classList.add("has-error");
    if (this.sizeError) this.sizeError.hidden = false;
    setTimeout(() => this._clearSizeError(), 2500);
  }

  _clearSizeError() {
    if (!this.sizeWrapper) return;
    this.sizeWrapper.classList.remove("has-error");
    if (this.sizeError) this.sizeError.hidden = true;
  }

  // ── Variant selection: feeds Dawn's own form + updates price/image ──
  _applyVariant(size) {
    const variant = this.variants.find((v) => v.size === size);
    if (!variant) return;

    // This is the field product-form.js actually reads on submit.
    if (this.variantInput) {
      this.variantInput.value = variant.id;
      this.variantInput.disabled = false;
    }

    // Availability: enable/disable the real submit button + its label.
    if (this.submitBtn) {
      this.submitBtn.disabled = !variant.available;
      const label = this.submitBtn.querySelector("span");
      if (label) label.textContent = variant.available ? "Add to Cart" : "Sold Out";
    }

    // Price swap.
    if (this.priceEl) this.priceEl.textContent = variant.price;
    if (this.comparePriceEl) {
      if (variant.onSale) {
        this.comparePriceEl.textContent = variant.comparePrice;
        this.comparePriceEl.hidden = false;
      } else {
        this.comparePriceEl.hidden = true;
      }
    }
    if (this.saleBadge) {
      this.saleBadge.style.display = variant.onSale ? "" : "none";
    }

    // Image swap — only if this variant has its own distinct image;
    // otherwise fall back to the product's default card image.
    if (this.mainImg) {
      const nextSrc = variant.image || this.mainImg.dataset.defaultSrc;
      if (nextSrc && this.mainImg.src !== nextSrc) {
        this.mainImg.src = nextSrc;
        this.mainImg.removeAttribute("srcset");
      }
    }
  }
}

customElements.define("product-card-fashionhub", ProductCardFashionhub);